import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { mapState } from "pinia";

import { PatientPayor } from "../../../../../../../resources-vue/vue-models/patient-payor";
import {
    ApplicationEnum,
    PayerSequenceCodes,
    PayerTypes,
    RelationshipToInsured
} from "../../../../../../../enums/enums";
import { CentralIntakeService } from "../../../../../../../services/central-intake.service";
import { ITypeaheadOptions } from "../../../../../../../interfaces/i-typeahead";
import { formatDateInput } from "../../../../../../../common/vue-helpers/modifiers/value-modifier";
import { enumToMap } from "../../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { useConvertToPendingPal } from "../../../../store/useConvertToPendingPal";
import { HospicePatientPayer } from "../../../../../../../resources-vue/vue-models/hospice-patient-payer";

import TypeaheadInput from "../../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import DatePicker from "../../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import FormSection from "../../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import NonMedicarePayer from "../NonMedicarePayer/NonMedicarePayer.vue";
import MedicarePayer from "../MedicarePayer/MedicarePayer.vue";

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput,
        DatePicker,
        NonMedicarePayer,
        MedicarePayer
    },

    validations() {
        return {
            payer: {
                AgencyPayorName: { required },
                EffectiveDate: { required },
                Type: { required }
            }
        };
    },
    props: {
        payer: {
            type: Object,
            default: new HospicePatientPayer()
        },
        isLoading: {
            type: Boolean
        },
        isAddForm: {
            type: Boolean,
            default: false
        },
        medicareIds: {
            type: Array
        }
    },

    data(vm) {
        return {
            _centralIntakeService: null,
            selectedPayor: null,
            initialValue: null,
            charityId: "",
            defaultMedicareIds: [],
            roomAndBoardPayors: [],
            showRoomAndBoardWarning: false,
            payerSequenceCodeEnum: PayerSequenceCodes,
            relationshipToInsuredEnum: RelationshipToInsured,
            payorTypeEnum: PayerTypes,
            roomAndBoardTypeValue: PayerTypes.enum.RoomAndBoard,
            formatDateInput,
            enumToMap,
            v$: useVuelidate()
        };
    },

    created() {
        if (!this.isAddForm) {
            this.initialValue = {
                name: this.payer.AgencyPayorName,
                value: this.payer.AgencyPayorId
            };
        }
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["patientDetails", "accountId", "application", "patientOptions"]),
        showSubscriberName() {
            if (!this.relationshipToInsuredEnum || !this.payer?.RelationshipToInsured) {
                return false;
            }
            const isRelationshipSelf = this.relationshipToInsuredEnum.enum.Self == this.payer.RelationshipToInsured;
            if (!this.payer.IsMedicare && !isRelationshipSelf) {
                return true;
            }
            return false;
        }
    },

    watch: {
        selectedPayor(newValue, oldValue) {
            this.selectedPayorChanged(newValue, oldValue);
        },
        payer: {
            handler() {
                this.payerChanged();
            },
            deep: true
        }
    },

    methods: {
        async getPayors(filter: string, limit: number) {
            const response = await this._centralIntakeService.getPayorsByName({
                name: filter,
                includeCharityPayor: true,
                providerIds: [this.patientOptions.data.providerId],
                application: this.application,
                agencyId: this.accountId,
                accountId: this.accountId
            });
            let charityPayor = response.find((payor: any) => payor.isCharityPayor);
            let medicarePayers = response.filter((payor: any) => payor.isMedicarePayor);
            this.roomAndBoardPayors = response.filter((payor: any) => !!payor.roomAndBoardBillAsType);
            if (charityPayor) {
                this.charityId = charityPayor.id;
            }
            if (medicarePayers?.length > 0) {
                this.defaultMedicareIds = medicarePayers.map((payer: any) => payer.id);
            }
            if (response) {
                return response.map((payor: any) => ({ name: payor.name, value: payor.id }));
            } else {
                console.error("Unable to Fetch");
                return [];
            }
        },
        payerChanged() {
            if (this.payer?.IsCharity) {
                this.charityId = this.payer.AgencyPayorId;
            }
            if (this.payer?.IsMedicare) {
                this.defaultMedicareIds = [this.payer.AgencyPayorId];
            }
            if (!!this.payer?.BillAsType) {
                this.roomAndBoardPayors = [
                    {
                        name: this.payer.AgencyPayorName,
                        id: this.payer.AgencyPayorId,
                        isMedicarePayor: this.payer.IsMedicare,
                        isCharityPayor: this.payer.IsMedicare,
                        roomAndBoardBillAsType: this.payer.BillAsType
                    }
                ];
            }
            if (this.payer?.AgencyPayorId) {
                this.selectedPayor = {
                    name: this.payer.AgencyPayorName,
                    value: this.payer.AgencyPayorId
                };
            } else {
                this.selectedPayor = {
                    name: "",
                    value: null
                };
            }
        },
        selectedPayorChanged(newPayor: ITypeaheadOptions, oldPayor: ITypeaheadOptions): void {
            if (!this.payer) {
                return;
            }
            if (newPayor) {
                this.payer.AgencyPayorName = newPayor.name;
                this.payer.AgencyPayorId = newPayor.value;
            } else {
                this.payer.AgencyPayorName = "";
                this.payer.AgencyPayorId = "";
            }
            this.payer.IsCharity = !!this.payer.AgencyPayorId ? this.payer.AgencyPayorId === this.charityId : false;
            this.updatePayor();
            this.checkRoomAndBoard();
        },
        updatePayor() {
            // TODO: remove medicare ID
            this.payer.IsMedicare = this.medicareIds.indexOf(this.payer.AgencyPayorId) > -1;
            let selectedRoomAndBoard = this.roomAndBoardPayors.find((payor) => payor.id === this.payer.AgencyPayorId);
            if (this.payer.Type === this.roomAndBoardTypeValue && !!selectedRoomAndBoard) {
                this.payer.BillAsType = selectedRoomAndBoard.roomAndBoardBillAsType;
            }
            if (this.payer.IsMedicare) {
                this.payer.RelationshipToInsured = 2;
            }
        },
        checkRoomAndBoard() {
            let roomAndBoardPayorIds = this.roomAndBoardPayors.map((payor) => payor.id);
            if (!this.selectedPayor?.value) {
                this.showRoomAndBoardWarning = false;
                return;
            }
            if (
                this.payer.Type === this.roomAndBoardTypeValue &&
                roomAndBoardPayorIds.indexOf(this.payer.AgencyPayorId) == -1
            ) {
                this.showRoomAndBoardWarning = true;
            } else {
                this.showRoomAndBoardWarning = false;
            }
        },
        payerTypeUpdated() {
            // Set time out to let the binding of payer type available
            setTimeout(() => {
                this.updatePayor();
                this.checkRoomAndBoard();
            }, 0);
        },
        hasDeductibleMetChanged(value: boolean) {
            this.payer.DeductibleMet = value;
        },
        outOfPocketMaxMetChanged(value: boolean) {
            this.payer.IsOutOfPocketMaxMet = value;
        },
        requireAuthorizationChanged(value: boolean) {
            this.payer.RequirePriorAuthorization = value;
        },
        selectedPayorChangedd(data: any) {
            this.selectedPayor = data;
        },
        changeEffectiveDate(date: any) {
            this.payer.EffectiveDate = date;
        },
        medicareDateChange(date: any) {
            this.payer.MedicarePartAEffectiveDate = date;
        },
        handleDateChange(e: any) {
            this.payer.EffectiveDate = e.target.value;
        }
    }
});
